// src/brand-space/text.ts
var cachedSegmenter;
var segmenter = () => cachedSegmenter ?? (cachedSegmenter = new Intl.Segmenter(void 0, { granularity: "grapheme" }));
var brandSpaceGraphemes = (value) => [...segmenter().segment(value)].map(({ segment }) => segment);
var takeGraphemes = (value, limit) => {
  const units = [];
  for (const { segment } of segmenter().segment(value)) {
    if (units.length === limit) return { units, exceeded: true };
    units.push(segment);
  }
  return { units, exceeded: false };
};
var exceedsBrandSpaceGraphemeLimit = (value, limit) => takeGraphemes(value, limit).exceeded;
var normalizeBrandSpaceText = (value) => value.replace(/\p{Cc}/gu, " ").replace(/\s+/g, " ").trim();
var truncateBrandSpaceMetadataText = (value, limit) => takeGraphemes(normalizeBrandSpaceText(value), limit).units.join("");
var TRAILING = /[\s,;:.!?…–—-]+$/u;
var ELLIPSIS = "\u2026";
function clampBrandSpaceDisplayText(value, limit) {
  if (limit <= 0) return "";
  const normalized = normalizeBrandSpaceText(value);
  const { units, exceeded } = takeGraphemes(normalized, limit);
  if (!exceeded) return normalized;
  const head = units.slice(0, limit - 1).join("");
  const boundary = head.lastIndexOf(" ");
  const cut = (boundary > 0 ? head.slice(0, boundary) : head).replace(TRAILING, "");
  return `${cut || head.trim()}${ELLIPSIS}`;
}

// src/brand-space/locale.ts
var BRAND_SPACE_PUBLIC_LOCALES = ["fr", "en", "it"];
var BRAND_SPACE_FALLBACK_LOCALE = "en";
function isBrandSpacePublicLocale(value) {
  return typeof value === "string" && BRAND_SPACE_PUBLIC_LOCALES.includes(value);
}
function coerceBrandSpacePublicLocale(value) {
  return isBrandSpacePublicLocale(value) ? value : BRAND_SPACE_FALLBACK_LOCALE;
}

// src/brand-space/config.ts
var BRAND_SPACE_CONFIG_VERSION = 5;
var BRAND_SPACE_SECTION_KEYS = [
  "profile",
  "links",
  "creatorContent",
  "affiliate"
];
var LEGACY_SECTION_KEYS = [...BRAND_SPACE_SECTION_KEYS, "stickyCta"];
var BRAND_SPACE_LAYOUTS = {
  profile: ["minimal", "bold", "editorial"],
  links: ["stack", "grid", "cards"],
  creatorContent: ["scroll", "grid", "mosaic"],
  affiliate: ["banner", "card", "inline"]
};
var LEGACY_STICKY_CTA_LAYOUTS = ["bar", "floating", "inline"];
var LEGACY_STICKY_CTA_LABEL_LIMIT = 160;
var BRAND_SPACE_FONT_PAIRINGS = [
  {
    id: "inter",
    label: "Inter",
    headingFamily: "'Inter', system-ui, sans-serif",
    bodyFamily: "'Inter', system-ui, sans-serif",
    googleFamilies: ["Inter:wght@400;500;600;700"]
  },
  {
    id: "cormorant",
    label: "Cormorant Garamond",
    headingFamily: "'Cormorant Garamond', Georgia, serif",
    bodyFamily: "'Cormorant Garamond', Georgia, serif",
    googleFamilies: ["Cormorant+Garamond:wght@400;500;600;700"]
  },
  {
    id: "inter+cormorant",
    label: "Cormorant + Inter",
    headingFamily: "'Cormorant Garamond', Georgia, serif",
    bodyFamily: "'Inter', system-ui, sans-serif",
    googleFamilies: [
      "Cormorant+Garamond:wght@500;600;700",
      "Inter:wght@400;500;600"
    ]
  },
  {
    id: "playfair+inter",
    label: "Playfair Display + Inter",
    headingFamily: "'Playfair Display', Georgia, serif",
    bodyFamily: "'Inter', system-ui, sans-serif",
    googleFamilies: ["Playfair+Display:wght@500;600;700", "Inter:wght@400;500;600"]
  },
  {
    id: "fraunces+inter",
    label: "Fraunces + Inter",
    headingFamily: "'Fraunces', Georgia, serif",
    bodyFamily: "'Inter', system-ui, sans-serif",
    googleFamilies: ["Fraunces:wght@500;600;700", "Inter:wght@400;500;600"]
  },
  {
    id: "dmserif+dmsans",
    label: "DM Serif + DM Sans",
    headingFamily: "'DM Serif Display', Georgia, serif",
    bodyFamily: "'DM Sans', sans-serif",
    googleFamilies: ["DM+Serif+Display", "DM+Sans:wght@400;500;600"]
  },
  {
    id: "lora+inter",
    label: "Lora + Inter",
    headingFamily: "'Lora', Georgia, serif",
    bodyFamily: "'Inter', system-ui, sans-serif",
    googleFamilies: ["Lora:wght@500;600;700", "Inter:wght@400;500;600"]
  },
  {
    id: "spectral+inter",
    label: "Spectral + Inter",
    headingFamily: "'Spectral', Georgia, serif",
    bodyFamily: "'Inter', system-ui, sans-serif",
    googleFamilies: ["Spectral:wght@500;600;700", "Inter:wght@400;500;600"]
  },
  {
    id: "libre+work",
    label: "Libre Baskerville + Work Sans",
    headingFamily: "'Libre Baskerville', Georgia, serif",
    bodyFamily: "'Work Sans', sans-serif",
    googleFamilies: ["Libre+Baskerville:wght@400;700", "Work+Sans:wght@400;500;600"]
  },
  {
    id: "poppins",
    label: "Poppins",
    headingFamily: "'Poppins', sans-serif",
    bodyFamily: "'Poppins', sans-serif",
    googleFamilies: ["Poppins:wght@400;500;600;700"]
  },
  {
    id: "montserrat",
    label: "Montserrat",
    headingFamily: "'Montserrat', sans-serif",
    bodyFamily: "'Montserrat', sans-serif",
    googleFamilies: ["Montserrat:wght@400;500;600;700"]
  },
  {
    id: "space",
    label: "Space Grotesk",
    headingFamily: "'Space Grotesk', sans-serif",
    bodyFamily: "'Space Grotesk', sans-serif",
    googleFamilies: ["Space+Grotesk:wght@400;500;600;700"]
  },
  {
    id: "syne",
    label: "Syne",
    headingFamily: "'Syne', sans-serif",
    bodyFamily: "'Syne', sans-serif",
    googleFamilies: ["Syne:wght@400;500;600;700;800"]
  },
  {
    id: "sora",
    label: "Sora",
    headingFamily: "'Sora', sans-serif",
    bodyFamily: "'Sora', sans-serif",
    googleFamilies: ["Sora:wght@400;500;600;700"]
  },
  {
    id: "archivo",
    label: "Archivo",
    headingFamily: "'Archivo', sans-serif",
    bodyFamily: "'Archivo', sans-serif",
    googleFamilies: ["Archivo:wght@400;500;600;700"]
  },
  {
    id: "epilogue",
    label: "Epilogue",
    headingFamily: "'Epilogue', sans-serif",
    bodyFamily: "'Epilogue', sans-serif",
    googleFamilies: ["Epilogue:wght@400;500;600;700"]
  }
];
var BRAND_SPACE_LINK_ICON_IDS = [
  "shoppingBag",
  "gift",
  "play",
  "mail",
  "link",
  "percentage",
  "globe",
  "heart",
  "star",
  "pin",
  "calendar",
  "camera",
  "musicNote",
  "book",
  "clipboardCheck"
];
var BRAND_SPACE_CREATOR_PLATFORMS = ["instagram", "tiktok"];
var BRAND_SPACE_APPLICATION_QUESTION_KINDS = [
  "short",
  "long",
  "choice",
  "multi",
  "number",
  "url"
];
var BRAND_SPACE_LIMITS = {
  displayName: 120,
  imageUrl: 2048,
  description: 1e3,
  category: 80,
  city: 120,
  socialUrl: 2048,
  links: 20,
  /**
   * Also the bound of a question key: both address one row of a keyed
   * collection through drafts, variant overrides and the published payload, and
   * both go through `isBrandSpaceItemKey`. One rule, one number.
   */
  linkKey: 64,
  linkLabel: 160,
  linkSubtitle: 240,
  linkUrl: 2048,
  affiliateHeading: 200,
  perks: 10,
  perk: 160,
  questions: 5,
  questionLabel: 200,
  questionOptionsMin: 2,
  questionOptionsMax: 8,
  questionOption: 80,
  /** Graphemes. */
  answerShort: 280,
  /** Graphemes. */
  answerLong: 1e3,
  /** A value ceiling, not a length. */
  answerNumber: 1e12,
  /** UTF-16 code units, like every other URL bound here. */
  answerUrl: 2048,
  programId: 128,
  manualContentIds: 6,
  contentId: 128
};
var BRAND_SPACE_PROFILE_DESCRIPTION_DISPLAY_LIMIT = 280;
var BRAND_SPACE_DEFAULT_COPY = {
  en: {
    affiliateHeading: "Join our affiliate program"
  },
  fr: {
    affiliateHeading: "Rejoignez notre programme"
  },
  it: {
    affiliateHeading: "Entra nel nostro programma"
  }
};
var FONT_PAIRING_IDS = new Set(BRAND_SPACE_FONT_PAIRINGS.map(({ id }) => id));
var LINK_ICON_IDS = new Set(BRAND_SPACE_LINK_ICON_IDS);
var SECTION_KEYS = new Set(BRAND_SPACE_SECTION_KEYS);
var DANGEROUS_KEYS = /* @__PURE__ */ new Set(["__proto__", "prototype", "constructor"]);
var objectHasOwn = Object.hasOwn;
function buildDefaultBrandSpaceConfig(seed = {}) {
  const displayName = seed.displayName ?? "";
  const websiteUrl = seed.websiteUrl?.trim() || null;
  return {
    version: BRAND_SPACE_CONFIG_VERSION,
    locale: seed.locale ?? null,
    programId: null,
    theme: {
      accent: "#7B5FC9",
      surface: "#FAF6F1",
      textTone: "dark",
      fontPair: "poppins",
      radius: "rounded",
      density: "comfortable"
    },
    sections: {
      order: ["profile", "links", "creatorContent", "affiliate"],
      visibility: {
        profile: true,
        links: true,
        creatorContent: true,
        affiliate: true
      },
      layouts: {
        profile: "editorial",
        links: "stack",
        creatorContent: "scroll",
        affiliate: "card"
      }
    },
    profile: {
      displayName,
      imageUrl: seed.imageUrl ?? null,
      description: seed.description ?? "",
      category: seed.category ?? "",
      city: seed.city ?? "",
      instagramUrl: seed.instagramUrl ?? null,
      tiktokUrl: seed.tiktokUrl ?? null
    },
    links: {
      items: websiteUrl ? [
        {
          key: "website",
          label: displayName || websiteUrl,
          url: websiteUrl,
          subtitle: "",
          badge: null,
          enabled: true,
          icon: "globe"
        }
      ] : []
    },
    creatorContent: {
      source: {
        mode: "auto",
        limit: 6,
        platforms: ["instagram", "tiktok"],
        manualContentIds: []
      }
    },
    affiliate: {
      heading: seed.locale ? BRAND_SPACE_DEFAULT_COPY[seed.locale].affiliateHeading : "",
      perks: [],
      questions: []
    }
  };
}
function deepFreeze(value) {
  if (value && typeof value === "object") {
    Object.freeze(value);
    for (const child of Object.values(value)) {
      deepFreeze(child);
    }
  }
  return value;
}
var DEFAULT_BRAND_SPACE_CONFIG = deepFreeze(buildDefaultBrandSpaceConfig());
function createDefaultBrandSpaceConfig(seed = {}) {
  return upgradeBrandSpaceConfig(buildDefaultBrandSpaceConfig(seed));
}
var BrandSpaceConfigUpgradeError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "BrandSpaceConfigUpgradeError";
  }
};
function isPlainRecord(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
function assertSafeJsonTree(value, seen = /* @__PURE__ */ new WeakSet()) {
  if (value === null || typeof value !== "object") return;
  if (seen.has(value)) {
    throw new BrandSpaceConfigUpgradeError(
      "invalid_config",
      "Invalid Brand Space configuration: cyclic or repeated object reference"
    );
  }
  seen.add(value);
  if (Array.isArray(value)) {
    for (const item2 of value) assertSafeJsonTree(item2, seen);
    return;
  }
  if (!isPlainRecord(value)) {
    throw new BrandSpaceConfigUpgradeError(
      "invalid_config",
      "Invalid Brand Space configuration: values must be plain JSON objects"
    );
  }
  for (const [key, child] of Object.entries(value)) {
    if (DANGEROUS_KEYS.has(key)) {
      throw new BrandSpaceConfigUpgradeError(
        "dangerous_key",
        `Invalid Brand Space configuration: dangerous key ${key}`
      );
    }
    assertSafeJsonTree(child, seen);
  }
}
function hasExactKeys(record, expected) {
  const keys = Object.keys(record);
  return keys.length === expected.length && keys.every((key) => expected.includes(key));
}
function isString(value) {
  return typeof value === "string";
}
function isStringArray(value) {
  return Array.isArray(value) && value.every(isString);
}
function isBoundedString(value, maxLength, allowEmpty = true) {
  return isString(value) && value.length <= maxLength && (allowEmpty || value.length > 0) && !/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/.test(value);
}
function isBrandSpaceGraphemeBoundedString(value, maxGraphemes, allowEmpty = true) {
  return isBoundedString(value, Number.MAX_SAFE_INTEGER, allowEmpty) && !exceedsBrandSpaceGraphemeLimit(value, maxGraphemes);
}
function isSafeBrandSpaceWebUrl(value, maxLength = 2048) {
  if (!isString(value) || value.length === 0 || value.length > maxLength || value.trim() !== value || /[\u0000-\u001F\u007F]/.test(value)) {
    return false;
  }
  try {
    const WebUrl = globalThis.URL;
    const parsed = new WebUrl(value);
    return (parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname.length > 0 && parsed.username.length === 0 && parsed.password.length === 0;
  } catch {
    return false;
  }
}
function isNullableSafeWebUrl(value, maxLength) {
  return value === null || isSafeBrandSpaceWebUrl(value, maxLength);
}
function isUniqueArray(values) {
  return new Set(values).size === values.length;
}
function brandSpaceOffersApplications(config) {
  return config.sections.visibility.affiliate;
}
function hasUniqueBrandSpaceItemKeys(items) {
  return isUniqueArray(items.map(({ key }) => key));
}
function hasUniqueBrandSpaceLinkKeys(items) {
  return hasUniqueBrandSpaceItemKeys(items);
}
var BRAND_SPACE_ITEM_KEY_PATTERN = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;
function isBrandSpaceItemKey(value) {
  return isBoundedString(value, BRAND_SPACE_LIMITS.linkKey, false) && BRAND_SPACE_ITEM_KEY_PATTERN.test(value);
}
function isTheme(value) {
  if (!isPlainRecord(value) || !hasExactKeys(value, ["accent", "surface", "textTone", "fontPair", "radius", "density"])) {
    return false;
  }
  const colorPattern = /^#[0-9a-fA-F]{6}$/;
  return isString(value.accent) && colorPattern.test(value.accent) && isString(value.surface) && colorPattern.test(value.surface) && (value.textTone === "dark" || value.textTone === "light") && isString(value.fontPair) && FONT_PAIRING_IDS.has(value.fontPair) && ["sharp", "soft", "rounded", "pill"].includes(value.radius) && ["compact", "comfortable", "airy"].includes(value.density);
}
function isSections(value) {
  return isSectionsOf(value, BRAND_SPACE_SECTION_KEYS);
}
function isLegacySections(value) {
  return isSectionsOf(value, LEGACY_SECTION_KEYS);
}
function isSectionsOf(value, keys) {
  if (!isPlainRecord(value) || !hasExactKeys(value, ["order", "visibility", "layouts"])) {
    return false;
  }
  const allowed = new Set(keys);
  if (!Array.isArray(value.order) || !value.order.every((key) => isString(key) && allowed.has(key)) || value.order.length !== keys.length || !isUniqueArray(value.order) || value.order[0] !== "profile") {
    return false;
  }
  if (!isPlainRecord(value.visibility) || !hasExactKeys(value.visibility, keys) || value.visibility.profile !== true || !Object.values(value.visibility).every((visible) => typeof visible === "boolean")) {
    return false;
  }
  if (!isPlainRecord(value.layouts) || !hasExactKeys(value.layouts, keys)) {
    return false;
  }
  return BRAND_SPACE_LAYOUTS.profile.includes(value.layouts.profile) && BRAND_SPACE_LAYOUTS.links.includes(value.layouts.links) && BRAND_SPACE_LAYOUTS.creatorContent.includes(
    value.layouts.creatorContent
  ) && BRAND_SPACE_LAYOUTS.affiliate.includes(value.layouts.affiliate) && (!allowed.has("stickyCta") || LEGACY_STICKY_CTA_LAYOUTS.includes(
    value.layouts.stickyCta
  ));
}
function isProfile(value) {
  return isPlainRecord(value) && hasExactKeys(value, [
    "displayName",
    "imageUrl",
    "description",
    "category",
    "city",
    "instagramUrl",
    "tiktokUrl"
  ]) && isBoundedString(value.displayName, BRAND_SPACE_LIMITS.displayName) && isNullableSafeWebUrl(value.imageUrl, BRAND_SPACE_LIMITS.imageUrl) && isBoundedString(value.description, BRAND_SPACE_LIMITS.description) && isBoundedString(value.category, BRAND_SPACE_LIMITS.category) && isBoundedString(value.city, BRAND_SPACE_LIMITS.city) && isNullableSafeWebUrl(value.instagramUrl, BRAND_SPACE_LIMITS.socialUrl) && isNullableSafeWebUrl(value.tiktokUrl, BRAND_SPACE_LIMITS.socialUrl);
}
function isLinkItem(value) {
  return isPlainRecord(value) && hasExactKeys(value, ["key", "label", "url", "subtitle", "badge", "enabled", "icon"]) && isBrandSpaceItemKey(value.key) && isBoundedString(value.label, BRAND_SPACE_LIMITS.linkLabel) && isSafeBrandSpaceWebUrl(value.url, BRAND_SPACE_LIMITS.linkUrl) && isBoundedString(value.subtitle, BRAND_SPACE_LIMITS.linkSubtitle) && (value.badge === null || value.badge === "new" || value.badge === "promo") && typeof value.enabled === "boolean" && isString(value.icon) && LINK_ICON_IDS.has(value.icon);
}
function isLinks(value) {
  if (!isPlainRecord(value) || !hasExactKeys(value, ["items"]) || !Array.isArray(value.items) || value.items.length > BRAND_SPACE_LIMITS.links || !value.items.every(isLinkItem)) {
    return false;
  }
  return hasUniqueBrandSpaceLinkKeys(value.items);
}
function isCreatorContent(value) {
  if (!isPlainRecord(value) || !hasExactKeys(value, ["source"]) || !isPlainRecord(value.source)) {
    return false;
  }
  const source = value.source;
  if (!hasExactKeys(source, ["mode", "limit", "platforms", "manualContentIds"])) return false;
  if (source.mode !== "auto" && source.mode !== "manual" || ![3, 4, 5, 6].includes(source.limit) || !Array.isArray(source.platforms) || source.platforms.length === 0 || !source.platforms.every(
    (platform) => platform === "instagram" || platform === "tiktok"
  ) || !isUniqueArray(source.platforms) || !isStringArray(source.manualContentIds) || source.manualContentIds.length > BRAND_SPACE_LIMITS.manualContentIds || !source.manualContentIds.every(
    (id) => isBoundedString(id, BRAND_SPACE_LIMITS.contentId, false)
  ) || !isUniqueArray(source.manualContentIds)) {
    return false;
  }
  return true;
}
var QUESTION_KINDS_WITH_OPTIONS = /* @__PURE__ */ new Set(["choice", "multi"]);
var QUESTION_KINDS = new Set(BRAND_SPACE_APPLICATION_QUESTION_KINDS);
function isBrandSpaceQuestionOption(value) {
  return isBrandSpaceGraphemeBoundedString(value, BRAND_SPACE_LIMITS.questionOption, false) && value.trim() === value;
}
function isBrandSpaceApplicationQuestion(value) {
  if (!isPlainRecord(value) || !hasExactKeys(value, ["key", "label", "kind", "required", "options"]) || !isBrandSpaceItemKey(value.key) || !isBrandSpaceGraphemeBoundedString(value.label, BRAND_SPACE_LIMITS.questionLabel) || !isString(value.kind) || !QUESTION_KINDS.has(value.kind) || typeof value.required !== "boolean" || !isStringArray(value.options)) {
    return false;
  }
  if (!QUESTION_KINDS_WITH_OPTIONS.has(value.kind)) return value.options.length === 0;
  return value.options.length >= BRAND_SPACE_LIMITS.questionOptionsMin && value.options.length <= BRAND_SPACE_LIMITS.questionOptionsMax && value.options.every(isBrandSpaceQuestionOption) && isUniqueArray(value.options);
}
function isQuestions(value) {
  return Array.isArray(value) && value.length <= BRAND_SPACE_LIMITS.questions && value.every(isBrandSpaceApplicationQuestion) && hasUniqueBrandSpaceItemKeys(value);
}
var AFFILIATE_KEYS = ["heading", "perks", "questions"];
var LEGACY_AFFILIATE_KEYS = ["heading", "perks"];
function isAffiliate(value) {
  return isAffiliateOf(value, AFFILIATE_KEYS);
}
function isLegacyAffiliate(value) {
  return isAffiliateOf(value, LEGACY_AFFILIATE_KEYS);
}
function isAffiliateOf(value, keys) {
  if (!isPlainRecord(value) || !hasExactKeys(value, keys) || !isBoundedString(value.heading, BRAND_SPACE_LIMITS.affiliateHeading) || !isStringArray(value.perks) || value.perks.length > BRAND_SPACE_LIMITS.perks || !value.perks.every((perk) => isBoundedString(perk, BRAND_SPACE_LIMITS.perk, false))) {
    return false;
  }
  return !keys.includes("questions") || isQuestions(value.questions);
}
function isBrandSpaceProgramId(value) {
  return value === null || isBoundedString(value, BRAND_SPACE_LIMITS.programId, false);
}
function isStickyCtaV2(value) {
  return isPlainRecord(value) && hasExactKeys(value, ["label"]) && isBoundedString(value.label, LEGACY_STICKY_CTA_LABEL_LIMIT);
}
function isStickyCtaV1(value) {
  return isPlainRecord(value) && hasExactKeys(value, ["label", "programId"]) && isBoundedString(value.label, LEGACY_STICKY_CTA_LABEL_LIMIT) && isBrandSpaceProgramId(value.programId);
}
function hasValidSharedSections(value, sections = isSections, affiliate = isAffiliate) {
  return isTheme(value.theme) && sections(value.sections) && isProfile(value.profile) && isLinks(value.links) && isCreatorContent(value.creatorContent) && affiliate(value.affiliate);
}
function isBrandSpaceConfig(value) {
  return isPlainRecord(value) && hasExactKeys(value, [
    "version",
    "locale",
    "programId",
    "theme",
    "sections",
    "profile",
    "links",
    "creatorContent",
    "affiliate"
  ]) && value.version === BRAND_SPACE_CONFIG_VERSION && (value.locale === null || isBrandSpacePublicLocale(value.locale)) && isBrandSpaceProgramId(value.programId) && hasValidSharedSections(value);
}
function isBrandSpaceConfigV4(value) {
  return isPlainRecord(value) && hasExactKeys(value, [
    "version",
    "programId",
    "theme",
    "sections",
    "profile",
    "links",
    "creatorContent",
    "affiliate"
  ]) && value.version === 4 && isBrandSpaceProgramId(value.programId) && hasValidSharedSections(value);
}
function isBrandSpaceConfigV3(value) {
  return isPlainRecord(value) && hasExactKeys(value, [
    "version",
    "programId",
    "theme",
    "sections",
    "profile",
    "links",
    "creatorContent",
    "affiliate"
  ]) && value.version === 3 && isBrandSpaceProgramId(value.programId) && hasValidSharedSections(value, isSections, isLegacyAffiliate);
}
function isBrandSpaceConfigV2(value) {
  return isPlainRecord(value) && hasExactKeys(value, [
    "version",
    "programId",
    "theme",
    "sections",
    "profile",
    "links",
    "creatorContent",
    "affiliate",
    "stickyCta"
  ]) && value.version === 2 && isBrandSpaceProgramId(value.programId) && hasValidSharedSections(value, isLegacySections, isLegacyAffiliate) && isStickyCtaV2(value.stickyCta);
}
function isBrandSpaceConfigV1(value) {
  return isPlainRecord(value) && hasExactKeys(value, [
    "version",
    "theme",
    "sections",
    "profile",
    "links",
    "creatorContent",
    "affiliate",
    "stickyCta"
  ]) && value.version === 1 && hasValidSharedSections(value, isLegacySections, isLegacyAffiliate) && isStickyCtaV1(value.stickyCta);
}
function cloneJson(value) {
  if (Array.isArray(value)) return value.map((item2) => cloneJson(item2));
  if (isPlainRecord(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, cloneJson(child)])
    );
  }
  return value;
}
function migrateBrandSpaceConfigV1ToV2(input) {
  const { version: _version, stickyCta, ...rest } = input;
  return {
    version: 2,
    programId: stickyCta.programId,
    ...rest,
    stickyCta: { label: stickyCta.label }
  };
}
function migrateBrandSpaceConfigV2ToV3(input) {
  const { version: _version, stickyCta: _stickyCta, sections, ...rest } = input;
  const { stickyCta: _visible, ...visibility } = sections.visibility;
  const { stickyCta: _layout, ...layouts } = sections.layouts;
  return {
    version: 3,
    ...rest,
    sections: {
      order: sections.order.filter((key) => key !== "stickyCta"),
      visibility,
      layouts
    }
  };
}
function migrateBrandSpaceConfigV3ToV4(input) {
  const { version: _version, affiliate, ...rest } = input;
  return {
    version: 4,
    ...rest,
    affiliate: { ...affiliate, questions: [] }
  };
}
function migrateBrandSpaceConfigV4ToV5(input) {
  const { version: _version, ...rest } = input;
  return {
    version: BRAND_SPACE_CONFIG_VERSION,
    locale: null,
    ...rest
  };
}
function upgradeBrandSpaceConfig(input) {
  assertSafeJsonTree(input);
  if (!isPlainRecord(input)) {
    throw new BrandSpaceConfigUpgradeError(
      "invalid_config",
      "Invalid Brand Space configuration: expected an object"
    );
  }
  if (!objectHasOwn(input, "version")) {
    throw new BrandSpaceConfigUpgradeError(
      "missing_version",
      "Invalid Brand Space configuration: missing schema version"
    );
  }
  if (!Number.isInteger(input.version)) {
    throw new BrandSpaceConfigUpgradeError(
      "invalid_version",
      "Invalid Brand Space configuration: schema version must be an integer"
    );
  }
  const version = input.version;
  if (version > BRAND_SPACE_CONFIG_VERSION) {
    throw new BrandSpaceConfigUpgradeError(
      "future_version",
      `Brand Space configuration version ${version} is newer than supported version ${BRAND_SPACE_CONFIG_VERSION}`
    );
  }
  if (version === BRAND_SPACE_CONFIG_VERSION) {
    if (!isBrandSpaceConfig(input)) {
      throw new BrandSpaceConfigUpgradeError(
        "invalid_config",
        "Invalid Brand Space configuration for the current schema version"
      );
    }
    return cloneJson(input);
  }
  if (version === 1) {
    if (!isBrandSpaceConfigV1(input)) {
      throw new BrandSpaceConfigUpgradeError(
        "invalid_config",
        "Invalid Brand Space configuration for schema version 1"
      );
    }
    return migrateBrandSpaceConfigV4ToV5(
      migrateBrandSpaceConfigV3ToV4(
        migrateBrandSpaceConfigV2ToV3(migrateBrandSpaceConfigV1ToV2(cloneJson(input)))
      )
    );
  }
  if (version === 2) {
    if (!isBrandSpaceConfigV2(input)) {
      throw new BrandSpaceConfigUpgradeError(
        "invalid_config",
        "Invalid Brand Space configuration for schema version 2"
      );
    }
    return migrateBrandSpaceConfigV4ToV5(
      migrateBrandSpaceConfigV3ToV4(migrateBrandSpaceConfigV2ToV3(cloneJson(input)))
    );
  }
  if (version === 3) {
    if (!isBrandSpaceConfigV3(input)) {
      throw new BrandSpaceConfigUpgradeError(
        "invalid_config",
        "Invalid Brand Space configuration for schema version 3"
      );
    }
    return migrateBrandSpaceConfigV4ToV5(
      migrateBrandSpaceConfigV3ToV4(cloneJson(input))
    );
  }
  if (version === 4) {
    if (!isBrandSpaceConfigV4(input)) {
      throw new BrandSpaceConfigUpgradeError(
        "invalid_config",
        "Invalid Brand Space configuration for schema version 4"
      );
    }
    return migrateBrandSpaceConfigV4ToV5(cloneJson(input));
  }
  throw new BrandSpaceConfigUpgradeError(
    "unsupported_version",
    `Brand Space configuration version ${version} has no registered upgrade path`
  );
}

// src/brand-space/overrides.ts
var BRAND_SPACE_OVERRIDE_PATHS = [
  /**
   * The published language. A variant that does not name one is served in the
   * page's, and follows it when the page changes — which is the point: variants
   * exist to address different audiences, so they have to be able to differ
   * here, and the ones that do not must not drift when the page is retranslated.
   */
  "locale",
  "programId",
  "theme",
  "theme.accent",
  "theme.surface",
  "theme.textTone",
  "theme.fontPair",
  "theme.radius",
  "theme.density",
  "sections",
  "sections.order",
  "sections.visibility",
  "sections.visibility.links",
  "sections.visibility.creatorContent",
  "sections.visibility.affiliate",
  "sections.layouts",
  "sections.layouts.profile",
  "sections.layouts.links",
  "sections.layouts.creatorContent",
  "sections.layouts.affiliate",
  "profile",
  "profile.displayName",
  "profile.imageUrl",
  "profile.description",
  "profile.category",
  "profile.city",
  "profile.instagramUrl",
  "profile.tiktokUrl",
  "links",
  "links.items",
  "creatorContent",
  "creatorContent.source",
  "creatorContent.source.mode",
  "creatorContent.source.limit",
  "creatorContent.source.platforms",
  "creatorContent.source.manualContentIds",
  "affiliate",
  "affiliate.heading",
  "affiliate.perks",
  /**
   * The whole question list, never one question. A variant that asks its own
   * questions replaces the list outright, the way `links.items` and
   * `affiliate.perks` already do: there is no addressing below an array here,
   * so a reordered or renamed question can never leave a variant pointing at a
   * row the page no longer has.
   */
  "affiliate.questions"
];
var LEGACY_STICKY_CTA_KEY = "stickyCta";
var LEGACY_PROGRAM_OVERRIDE_KEY = "programId";
var BrandSpaceOverrideError = class extends Error {
  constructor(code, message, cause) {
    super(message);
    this.code = code;
    this.name = "BrandSpaceOverrideError";
    this.cause = cause;
  }
};
var OVERRIDE_PATHS = new Set(BRAND_SPACE_OVERRIDE_PATHS);
var DANGEROUS_KEYS2 = /* @__PURE__ */ new Set(["__proto__", "prototype", "constructor"]);
var objectHasOwn2 = Object.hasOwn;
function isPlainRecord2(value) {
  if (value === null || typeof value !== "object" || Array.isArray(value)) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
function cloneJson2(value) {
  if (Array.isArray(value)) return value.map((item2) => cloneJson2(item2));
  if (isPlainRecord2(value)) {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, cloneJson2(child)])
    );
  }
  return value;
}
function assertOverrideTree(value, prefix = "") {
  if (!isPlainRecord2(value)) {
    throw new BrandSpaceOverrideError(
      "invalid_override",
      "Brand Space overrides must be a plain object"
    );
  }
  for (const [key, child] of Object.entries(value)) {
    if (DANGEROUS_KEYS2.has(key)) {
      throw new BrandSpaceOverrideError(
        "dangerous_key",
        `Brand Space override contains dangerous key ${key}`
      );
    }
    const path = prefix ? `${prefix}.${key}` : key;
    if (!OVERRIDE_PATHS.has(path)) {
      throw new BrandSpaceOverrideError(
        "unknown_path",
        `Brand Space override contains unknown path ${path}`
      );
    }
    if (isPlainRecord2(child)) assertOverrideTree(child, path);
  }
}
function assertSafeOverrideJson(value, seen = /* @__PURE__ */ new WeakSet(), isRoot = true) {
  if (value === null || typeof value !== "object") return;
  if (seen.has(value)) {
    throw new BrandSpaceOverrideError(
      "invalid_override",
      "Brand Space overrides must be an acyclic JSON value"
    );
  }
  seen.add(value);
  if (Array.isArray(value)) {
    for (const child of value) assertSafeOverrideJson(child, seen, false);
    return;
  }
  if (!isPlainRecord2(value)) {
    throw new BrandSpaceOverrideError(
      "invalid_override",
      "Brand Space overrides must contain only plain JSON objects"
    );
  }
  if (!isRoot && Object.keys(value).length === 0) {
    throw new BrandSpaceOverrideError(
      "invalid_override",
      "Brand Space overrides cannot contain empty object containers"
    );
  }
  for (const [key, child] of Object.entries(value)) {
    if (DANGEROUS_KEYS2.has(key)) {
      throw new BrandSpaceOverrideError(
        "dangerous_key",
        `Brand Space override contains dangerous key ${key}`
      );
    }
    assertSafeOverrideJson(child, seen, false);
  }
}
function mergeRecords(base, override) {
  const result = cloneJson2(base);
  for (const [key, value] of Object.entries(override)) {
    const baseValue = result[key];
    result[key] = isPlainRecord2(baseValue) && isPlainRecord2(value) ? mergeRecords(baseValue, value) : cloneJson2(value);
  }
  return result;
}
function migrateLegacyOverride(value) {
  const result = { ...value };
  const parent = result[LEGACY_STICKY_CTA_KEY];
  if (isPlainRecord2(parent) && objectHasOwn2(parent, LEGACY_PROGRAM_OVERRIDE_KEY)) {
    if (!objectHasOwn2(result, LEGACY_PROGRAM_OVERRIDE_KEY)) {
      result[LEGACY_PROGRAM_OVERRIDE_KEY] = parent[LEGACY_PROGRAM_OVERRIDE_KEY];
    }
  }
  delete result[LEGACY_STICKY_CTA_KEY];
  const sections = result.sections;
  if (isPlainRecord2(sections)) {
    const pruned = {};
    for (const [group, entries] of Object.entries(sections)) {
      if (!isPlainRecord2(entries)) {
        pruned[group] = entries;
        continue;
      }
      const { [LEGACY_STICKY_CTA_KEY]: _dropped, ...rest } = entries;
      if (Object.keys(rest).length > 0) pruned[group] = rest;
    }
    if (Object.keys(pruned).length > 0) result.sections = pruned;
    else delete result.sections;
  }
  return result;
}
function upgradeBrandSpaceConfigOverride(value) {
  assertSafeOverrideJson(value);
  const upgraded = migrateLegacyOverride(cloneJson2(value));
  assertOverrideTree(upgraded);
  try {
    upgradeBrandSpaceConfig(
      mergeRecords(
        createDefaultBrandSpaceConfig(),
        upgraded
      )
    );
  } catch (error) {
    if (error instanceof BrandSpaceOverrideError) throw error;
    throw new BrandSpaceOverrideError(
      "invalid_override",
      "Brand Space override contains a value outside the configuration contract",
      error
    );
  }
  return upgraded;
}
function mergeBrandSpaceConfig(base, override) {
  const validatedBase = upgradeBrandSpaceConfig(base);
  const upgradedOverride = upgradeBrandSpaceConfigOverride(override);
  const merged = mergeRecords(
    validatedBase,
    upgradedOverride
  );
  try {
    return upgradeBrandSpaceConfig(merged);
  } catch (error) {
    throw new BrandSpaceOverrideError(
      "invalid_override",
      "Brand Space override does not produce a valid configuration",
      error
    );
  }
}
function valuesEqual(left, right) {
  if (Object.is(left, right)) return true;
  if (Array.isArray(left) || Array.isArray(right)) {
    return Array.isArray(left) && Array.isArray(right) && left.length === right.length && left.every((value, index) => valuesEqual(value, right[index]));
  }
  if (!isPlainRecord2(left) || !isPlainRecord2(right)) return false;
  const leftKeys = Object.keys(left);
  const rightKeys = Object.keys(right);
  return leftKeys.length === rightKeys.length && leftKeys.every((key) => objectHasOwn2(right, key) && valuesEqual(left[key], right[key]));
}
function diffRecords(base, edited) {
  const result = {};
  for (const [key, editedValue] of Object.entries(edited)) {
    const baseValue = base[key];
    if (valuesEqual(baseValue, editedValue)) continue;
    if (isPlainRecord2(baseValue) && isPlainRecord2(editedValue)) {
      const nested = diffRecords(baseValue, editedValue);
      if (Object.keys(nested).length > 0) result[key] = nested;
    } else {
      result[key] = cloneJson2(editedValue);
    }
  }
  return result;
}
function diffBrandSpaceConfig(base, edited) {
  const validatedBase = upgradeBrandSpaceConfig(base);
  const validatedEdited = upgradeBrandSpaceConfig(edited);
  const { version: _baseVersion, ...baseContent } = validatedBase;
  const { version: _editedVersion, ...editedContent } = validatedEdited;
  return diffRecords(baseContent, editedContent);
}
function removeBrandSpaceOverride(override, path) {
  const upgraded = upgradeBrandSpaceConfigOverride(override);
  if (!OVERRIDE_PATHS.has(path)) {
    const dangerous = path.split(".").some((part) => DANGEROUS_KEYS2.has(part));
    throw new BrandSpaceOverrideError(
      dangerous ? "dangerous_key" : "unknown_path",
      `Brand Space override path is not allowed: ${path}`
    );
  }
  const result = cloneJson2(upgraded);
  const parts = path.split(".");
  const ancestors = [];
  let current = result;
  for (let index = 0; index < parts.length - 1; index += 1) {
    const key = parts[index];
    const child = current[key];
    if (!isPlainRecord2(child)) return result;
    ancestors.push({ parent: current, key });
    current = child;
  }
  delete current[parts[parts.length - 1]];
  for (let index = ancestors.length - 1; index >= 0; index -= 1) {
    const { parent, key } = ancestors[index];
    const child = parent[key];
    if (isPlainRecord2(child) && Object.keys(child).length === 0) delete parent[key];
    else break;
  }
  return result;
}
function countLeaves(value) {
  if (Array.isArray(value) || !isPlainRecord2(value)) return 1;
  return Object.values(value).reduce((total, child) => total + countLeaves(child), 0);
}
function countBrandSpaceOverrides(override) {
  const upgraded = upgradeBrandSpaceConfigOverride(override);
  if (Object.keys(upgraded).length === 0) return 0;
  return Object.values(upgraded).reduce(
    (total, child) => total + countLeaves(child),
    0
  );
}

// src/brand-space/address.ts
var BRAND_SPACE_ADDRESS_MAX_LENGTH = 80;
var BRAND_SPACE_CONFIG_MAX_BYTES = 3e4;
var BRAND_SPACE_OVERRIDE_MAX_BYTES = 16e3;
var BRAND_SPACE_CONFIG_DB_MAX_BYTES = 32e3;
var BRAND_SPACE_OVERRIDE_DB_MAX_BYTES = 18e3;
var BRAND_SPACE_STATUSES = ["draft", "published", "archived"];
var BRAND_SPACE_RESERVED_SEGMENTS = [
  "admin",
  "api",
  "apply",
  "assets",
  "auth",
  "b",
  "brand-space-api",
  "brand-space-preview",
  "brand-space-static",
  "c",
  "commissions",
  "dashboard",
  "edit",
  "health",
  "invitation",
  "invitations",
  "legal",
  "login",
  "new",
  "null",
  "og",
  "onboarding",
  "plans",
  "preview",
  "privacy",
  "program",
  "programme",
  "programs",
  "rights",
  "settings",
  "signup",
  "studio",
  "support",
  "terms",
  "undefined",
  "www"
];
var RESERVED_SEGMENTS = new Set(BRAND_SPACE_RESERVED_SEGMENTS);
var ASCII_KEBAB_SEGMENT = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
function classifyBrandSpaceAddressSegment(input) {
  const normalized = input.trim().toLowerCase();
  if (normalized.length === 0 || normalized.length > BRAND_SPACE_ADDRESS_MAX_LENGTH || !ASCII_KEBAB_SEGMENT.test(normalized)) {
    return { kind: "invalid", normalized: null };
  }
  if (RESERVED_SEGMENTS.has(normalized)) {
    return { kind: "reserved", normalized };
  }
  return { kind: "valid", normalized };
}
function normalizeBrandSpaceAddressSegment(input) {
  const classification = classifyBrandSpaceAddressSegment(input);
  return classification.kind === "valid" ? classification.normalized : null;
}
var BRAND_SPACE_PROGRAMME_SEGMENT = "programme";
function parseBrandSpacePublicAddress(input) {
  if (!Array.isArray(input) || input.length < 1 || input.length > 3) return null;
  if (!input.every((segment) => typeof segment === "string")) return null;
  const segments = [...input];
  let view = "home";
  if (segments.length > 1 && segments[segments.length - 1] === BRAND_SPACE_PROGRAMME_SEGMENT) {
    segments.pop();
    view = "programme";
  }
  if (segments.length > 2) return null;
  const [rawSlug, rawSuffix] = segments;
  const slug = normalizeBrandSpaceAddressSegment(rawSlug);
  if (!slug || slug !== rawSlug) return null;
  if (rawSuffix === void 0) return { slug, suffix: null, view };
  const suffix = normalizeBrandSpaceAddressSegment(rawSuffix);
  if (!suffix || suffix !== rawSuffix) return null;
  return { slug, suffix, view };
}

// src/brand-space/origin.ts
var BRAND_SPACE_DEFAULT_PUBLIC_ORIGIN = "https://space.fluenceur.com";

// src/brand-space/storage.ts
var BrandSpaceStorageError = class extends Error {
  constructor(code, message) {
    super(message);
    this.code = code;
    this.name = "BrandSpaceStorageError";
  }
};
function brandSpaceUtf8ByteLength(serialized) {
  let bytes = 0;
  for (const character of serialized) {
    const codePoint = character.codePointAt(0) ?? 0;
    bytes += codePoint <= 127 ? 1 : codePoint <= 2047 ? 2 : codePoint <= 65535 ? 3 : 4;
  }
  return bytes;
}
function brandSpaceJsonByteLength(value) {
  const serialized = JSON.stringify(value);
  return serialized === void 0 ? 0 : brandSpaceUtf8ByteLength(serialized);
}
function cloneJson3(value) {
  if (Array.isArray(value)) return value.map((item2) => cloneJson3(item2));
  if (value !== null && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, child]) => [key, cloneJson3(child)])
    );
  }
  return value;
}
function validateBrandSpaceConfigStorage(value) {
  const config = upgradeBrandSpaceConfig(value);
  if (brandSpaceJsonByteLength(config) > BRAND_SPACE_CONFIG_MAX_BYTES) {
    throw new BrandSpaceStorageError(
      "config_too_large",
      `Brand Space configuration exceeds ${BRAND_SPACE_CONFIG_MAX_BYTES} UTF-8 bytes`
    );
  }
  return config;
}
function validateBrandSpaceOverrideStorage(value) {
  const upgraded = upgradeBrandSpaceConfigOverride(value);
  if (brandSpaceJsonByteLength(upgraded) > BRAND_SPACE_OVERRIDE_MAX_BYTES) {
    throw new BrandSpaceStorageError(
      "override_too_large",
      `Brand Space override exceeds ${BRAND_SPACE_OVERRIDE_MAX_BYTES} UTF-8 bytes`
    );
  }
  return cloneJson3(upgraded);
}

// src/brand-space/public.ts
var BRAND_SPACE_PUBLIC_API_PATH = "/brand-spaces";
var BRAND_SPACE_INTERNAL_API_PATH = "/internal/brand-spaces";
var BRAND_SPACE_SITEMAP_API_PATH = "/internal/brand-spaces/sitemap";
var BRAND_SPACE_VARIANT_V1_CAP = 20;
var BRAND_SPACE_REVALIDATION_ADDRESS_CAP = 21;
var BRAND_SPACE_SITEMAP_ENTRY_CAP = 5e4;
var BRAND_SPACE_PRESENTATION_LIMITS = {
  items: 6,
  itemId: 128,
  creatorLabel: 160,
  alt: 240,
  imageUrl: 2048,
  videoUrl: 2048,
  destinationUrl: 2048
};
var BRAND_SPACE_LEGACY_PUBLIC_PATH = "/b";
var BRAND_SPACE_VARIANTS_API_SEGMENT = "variants";
var BRAND_SPACE_PREVIEW_API_SEGMENT = "preview";
var BRAND_SPACE_PREVIEW_TOKEN_API_PATH = "/dashboard/brand-spaces/preview-token";
var BRAND_SPACE_PREVIEW_HEADER = "x-brand-space-preview";
var BRAND_SPACE_PUBLIC_CACHE_CONTROL = "public, max-age=60, s-maxage=300, stale-while-revalidate=60";
var BRAND_SPACE_PRIVATE_CACHE_CONTROL = "private, no-store";
function toBrandSpacePublicConfig(config) {
  const { programId: _programId, ...rest } = config;
  return rest;
}
function validateBrandSpacePublicConfigStorage(input) {
  if (!input || typeof input !== "object" || Array.isArray(input)) {
    throw new TypeError("Invalid Brand Space public config");
  }
  if (Object.prototype.hasOwnProperty.call(input, "programId")) {
    throw new TypeError("Invalid Brand Space public config");
  }
  const source = input;
  const legacy = readLegacyStickyCta(source);
  const candidate = validateBrandSpaceConfigStorage(
    legacy ? (
      // Rebuilt at its own version so the shared upgrade path migrates it,
      // which is also what strips the sticky CTA.
      source.version === 1 ? { ...source, stickyCta: { label: legacy.label, programId: legacy.programId } } : { ...source, programId: legacy.programId, stickyCta: { label: legacy.label } }
    ) : { ...source, programId: null }
  );
  return toBrandSpacePublicConfig(candidate);
}
function readLegacyStickyCta(source) {
  if (!Object.prototype.hasOwnProperty.call(source, "stickyCta")) return null;
  const stickyCta = source.stickyCta;
  if (!stickyCta || typeof stickyCta !== "object" || Array.isArray(stickyCta)) {
    throw new TypeError("Invalid Brand Space public sticky CTA");
  }
  const keys = Object.keys(stickyCta);
  const { label, applicationConfigured } = stickyCta;
  if (keys.length !== 2 || !keys.includes("label") || !keys.includes("applicationConfigured") || typeof label !== "string" || typeof applicationConfigured !== "boolean") {
    throw new TypeError("Invalid Brand Space public sticky CTA");
  }
  return { label, programId: applicationConfigured ? "application-configured" : null };
}
function buildBrandSpacePublicPath(slug, suffix = null, view = "home") {
  const address = `/${slug}${suffix ? `/${suffix}` : ""}`;
  return view === "programme" ? `${address}/${BRAND_SPACE_PROGRAMME_SEGMENT}` : address;
}
function buildBrandSpaceLegacyPublicPath(slug, suffix = null, view = "home") {
  return `${BRAND_SPACE_LEGACY_PUBLIC_PATH}${buildBrandSpacePublicPath(slug, suffix, view)}`;
}

// src/brand-space/creator-content.ts
var HOSTED_MENTION_THUMBNAIL_PATH = {
  head: "/hosted-mentions/",
  tail: "/thumbnail.jpg"
};
var HOSTED_MENTION_THUMBNAIL_FILENAME = HOSTED_MENTION_THUMBNAIL_PATH.tail.slice(1);
var HOSTED_MENTION_VIDEO_FILENAME = "video.mp4";
function buildHostedMentionThumbnailUrl(mediaHost, platform, mentionId) {
  const { head, tail } = HOSTED_MENTION_THUMBNAIL_PATH;
  return `https://${mediaHost}${head}${platform}/${mentionId}${tail}`;
}
function buildHostedMentionPath(platform, mentionId, filename) {
  const prefix = HOSTED_MENTION_THUMBNAIL_PATH.head.slice(1);
  return `${prefix}${platform}/${mentionId}/${filename}`;
}
function buildHostedMentionVideoUrl(mediaHost, platform, mentionId) {
  const path = buildHostedMentionPath(
    platform,
    mentionId,
    HOSTED_MENTION_VIDEO_FILENAME
  );
  return `https://${mediaHost}/${path}`;
}
function brandSpaceHarvestedPlatforms(source) {
  return source.mode === "manual" ? BRAND_SPACE_CREATOR_PLATFORMS : source.platforms;
}
function plain(value) {
  if (value === null || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}
function exact(value, keys) {
  const own = Object.keys(value);
  return own.length === keys.length && own.every((key) => keys.includes(key));
}
function bounded(value, limit) {
  return typeof value === "string" && value.length > 0 && value.length <= limit;
}
function trimmedNonblank(value, limit) {
  return bounded(value, limit) && value.trim() === value && value.trim().length > 0;
}
var CREATOR_ITEM_ID_PATTERN = new RegExp(
  `^[A-Za-z0-9-]{1,${BRAND_SPACE_PRESENTATION_LIMITS.itemId}}$`
);
function validCreatorItemId(value) {
  return typeof value === "string" && CREATOR_ITEM_ID_PATTERN.test(value);
}
function validDestination(value) {
  if (value === void 0 || value === null) return true;
  if (!bounded(value, BRAND_SPACE_PRESENTATION_LIMITS.destinationUrl))
    return false;
  return isSafeBrandSpaceWebUrl(
    value,
    BRAND_SPACE_PRESENTATION_LIMITS.destinationUrl
  );
}
var isBrandSpaceMediaHost = (value) => typeof value === "string" && /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)(?:\.(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?))+$/.test(
  value
);
function validLikeCount(value) {
  return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
function item(value, mediaHost) {
  if (!plain(value)) return null;
  const keys = ["id", "imageUrl", "alt", "platform", "creatorLabel"];
  if (Object.prototype.hasOwnProperty.call(value, "destinationUrl"))
    keys.push("destinationUrl");
  if (Object.prototype.hasOwnProperty.call(value, "likeCount"))
    keys.push("likeCount");
  const carriesVideo = Object.prototype.hasOwnProperty.call(value, "videoUrl");
  if (carriesVideo) keys.push("videoUrl");
  if (!exact(value, keys)) return null;
  if (!validCreatorItemId(value.id) || !bounded(value.imageUrl, BRAND_SPACE_PRESENTATION_LIMITS.imageUrl) || // The clip is an address of the same family on the same host, so it is held
  // to its own length bound before anything else looks at it.
  carriesVideo && !bounded(value.videoUrl, BRAND_SPACE_PRESENTATION_LIMITS.videoUrl) || !trimmedNonblank(value.alt, BRAND_SPACE_PRESENTATION_LIMITS.alt) || !trimmedNonblank(
    value.creatorLabel,
    BRAND_SPACE_PRESENTATION_LIMITS.creatorLabel
  ) || value.platform !== "instagram" && value.platform !== "tiktok" || !validDestination(value.destinationUrl))
    return null;
  const expectedImage = buildHostedMentionThumbnailUrl(
    mediaHost,
    value.platform,
    value.id
  );
  if (value.imageUrl !== expectedImage) return null;
  if (carriesVideo && value.videoUrl !== buildHostedMentionVideoUrl(mediaHost, value.platform, value.id))
    return null;
  const likeCount = Object.prototype.hasOwnProperty.call(value, "likeCount") ? validLikeCount(value.likeCount) ? value.likeCount : null : void 0;
  return {
    id: value.id,
    imageUrl: value.imageUrl,
    alt: value.alt,
    platform: value.platform,
    creatorLabel: value.creatorLabel,
    ...Object.prototype.hasOwnProperty.call(value, "destinationUrl") ? { destinationUrl: value.destinationUrl } : {},
    ...carriesVideo ? { videoUrl: value.videoUrl } : {},
    ...likeCount === void 0 ? {} : { likeCount }
  };
}
function validateBrandSpaceCreatorContentPresentation(value, options) {
  try {
    if (!isBrandSpaceMediaHost(options.mediaHost) || !plain(value) || !exact(value, ["status", "items"]) || !Array.isArray(value.items) || value.items.length > BRAND_SPACE_PRESENTATION_LIMITS.items || !["idle", "loading", "error", "ready"].includes(value.status))
      return null;
    const seen = /* @__PURE__ */ new Set();
    const items = [];
    for (const input of value.items) {
      const parsed = item(input, options.mediaHost);
      if (!parsed || seen.has(parsed.id)) return null;
      seen.add(parsed.id);
      items.push(parsed);
    }
    return {
      status: value.status,
      items
    };
  } catch {
    return null;
  }
}

// src/brand-space/preview.ts
var BRAND_SPACE_PREVIEW_CHANNEL = "fluenceur.brand-space.preview";
var BRAND_SPACE_PREVIEW_VERSION = 1;
var BRAND_SPACE_PREVIEW_MESSAGE_MAX_BYTES = 65536;
var BrandSpacePreviewProtocolError = class extends Error {
  constructor(message) {
    super(message);
    this.name = "BrandSpacePreviewProtocolError";
  }
};
var plain2 = (value) => {
  if (value === null || typeof value !== "object") return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};
var exact2 = (value, keys) => {
  const own = Object.keys(value);
  return own.length === keys.length && own.every((key) => keys.includes(key));
};
var closed = (value, required, optional) => {
  const own = Object.keys(value);
  return required.every((key) => own.includes(key)) && own.every((key) => required.includes(key) || optional.includes(key));
};
var isBrandSpacePreviewInstanceId = (value) => typeof value === "string" && /^[A-Za-z0-9_-]{43}$/.test(value);
function normalizeBrandSpaceStudioOrigin(value) {
  if (typeof value !== "string" || !value || value.trim() !== value) return null;
  try {
    const WebUrl = globalThis.URL;
    const url = new WebUrl(value);
    const local = url.hostname === "localhost" || url.hostname === "127.0.0.1" || url.hostname === "[::1]" || url.hostname.endsWith(".localhost");
    if (url.origin !== value || url.pathname !== "/" || url.search || url.hash || url.username || url.password) return null;
    return url.protocol === "https:" || url.protocol === "http:" && local ? url.origin : null;
  } catch {
    return null;
  }
}
function createBrandSpacePreviewReadyMessage(instanceId) {
  return { channel: BRAND_SPACE_PREVIEW_CHANNEL, version: BRAND_SPACE_PREVIEW_VERSION, type: "READY", instanceId };
}
function createBrandSpacePreviewAppliedMessage(instanceId, sequence) {
  return { channel: BRAND_SPACE_PREVIEW_CHANNEL, version: BRAND_SPACE_PREVIEW_VERSION, type: "APPLIED", instanceId, sequence };
}
function createBrandSpacePreviewRenderMessage(instanceId, sequence, payload) {
  if (!isBrandSpacePreviewInstanceId(instanceId) || !Number.isSafeInteger(sequence) || sequence < 0) {
    throw new BrandSpacePreviewProtocolError("Invalid Brand Space preview render identity");
  }
  try {
    const serialized = JSON.stringify({
      channel: BRAND_SPACE_PREVIEW_CHANNEL,
      version: BRAND_SPACE_PREVIEW_VERSION,
      type: "RENDER",
      instanceId,
      sequence,
      payload
    });
    if (brandSpaceUtf8ByteLength(serialized) > BRAND_SPACE_PREVIEW_MESSAGE_MAX_BYTES) {
      throw new BrandSpacePreviewProtocolError("Brand Space preview render exceeds the transport budget");
    }
    return JSON.parse(serialized);
  } catch (error) {
    if (error instanceof BrandSpacePreviewProtocolError) throw error;
    throw new BrandSpacePreviewProtocolError("Brand Space preview render is not serializable");
  }
}
function parseBrandSpacePreviewControlMessage(input) {
  try {
    const serialized = JSON.stringify(input);
    if (serialized === void 0 || brandSpaceUtf8ByteLength(serialized) > BRAND_SPACE_PREVIEW_MESSAGE_MAX_BYTES || !plain2(input) || input.type !== "READY" && input.type !== "APPLIED") return { ok: false };
    const keys = input.type === "READY" ? ["channel", "version", "type", "instanceId"] : ["channel", "version", "type", "instanceId", "sequence"];
    if (!exact2(input, keys) || input.channel !== BRAND_SPACE_PREVIEW_CHANNEL || input.version !== BRAND_SPACE_PREVIEW_VERSION || !isBrandSpacePreviewInstanceId(input.instanceId) || input.type === "APPLIED" && (!Number.isSafeInteger(input.sequence) || input.sequence < 0)) return { ok: false };
    return { ok: true, value: JSON.parse(serialized) };
  } catch {
    return { ok: false };
  }
}
function parseBrandSpacePreviewRenderMessage(input, options) {
  try {
    const serialized = JSON.stringify(input);
    if (serialized === void 0 || brandSpaceUtf8ByteLength(serialized) > BRAND_SPACE_PREVIEW_MESSAGE_MAX_BYTES || !plain2(input) || !exact2(input, ["channel", "version", "type", "instanceId", "sequence", "payload"])) return { ok: false };
    if (input.channel !== BRAND_SPACE_PREVIEW_CHANNEL || input.version !== BRAND_SPACE_PREVIEW_VERSION || input.type !== "RENDER" || input.instanceId !== options.instanceId || !isBrandSpacePreviewInstanceId(input.instanceId) || !Number.isSafeInteger(input.sequence) || input.sequence < 0 || !isBrandSpaceMediaHost(options.mediaHost) || !plain2(input.payload) || !closed(input.payload, ["config", "locale", "creatorContent", "view"], ["stickyCtaGhost"]) || !isBrandSpacePublicLocale(input.payload.locale) || input.payload.view !== "home" && input.payload.view !== "programme" || input.payload.stickyCtaGhost !== void 0 && typeof input.payload.stickyCtaGhost !== "boolean") return { ok: false };
    const config = validateBrandSpaceConfigStorage(input.payload.config);
    const creatorContent = validateBrandSpaceCreatorContentPresentation(input.payload.creatorContent, { mediaHost: options.mediaHost });
    if (!creatorContent) return { ok: false };
    return { ok: true, value: { channel: BRAND_SPACE_PREVIEW_CHANNEL, version: BRAND_SPACE_PREVIEW_VERSION, type: "RENDER", instanceId: input.instanceId, sequence: input.sequence, payload: { config, locale: input.payload.locale, creatorContent, view: input.payload.view } } };
  } catch {
    return { ok: false };
  }
}

// src/brand-space/studio.ts
var BRAND_SPACE_STUDIO_API_PATH = "/dashboard/brand-spaces/studio";
var BRAND_SPACE_STUDIO_MENTIONS_API_PATH = "/dashboard/brand-spaces/studio/mentions";
var BRAND_SPACE_STUDIO_DRAFT_API_PATH = "/dashboard/brand-spaces/studio/draft";
var BRAND_SPACE_STUDIO_SLUG_AVAILABILITY_API_PATH = "/dashboard/brand-spaces/studio/slug-availability";
var BRAND_SPACE_STUDIO_PUBLISH_API_PATH = "/dashboard/brand-spaces/studio/publish";
var BRAND_SPACE_STUDIO_VARIANTS_API_PATH = "/dashboard/brand-spaces/studio/variants";
var BRAND_SPACE_STUDIO_MENTION_SORTS = ["recent", "likes"];
var BRAND_SPACE_STUDIO_MENTIONS_PAGE_SIZE = 36;
var BRAND_SPACE_STUDIO_MENTIONS_MAX_PAGE = 200;
var BRAND_SPACE_STUDIO_MENTION_THUMBNAIL_KINDS = [
  "image",
  "video"
];
function toBrandSpaceStudioCreatorContentCandidate(mention) {
  if (mention.publishableImageUrl === null) return null;
  return {
    id: mention.id,
    imageUrl: mention.publishableImageUrl,
    alt: mention.alt,
    platform: mention.platform,
    creatorLabel: mention.creatorLabel
  };
}
function normalizeBrandSpaceMentionHandleQuery(value) {
  return (value ?? "").trim().replace(/^@+/, "").trim().toLowerCase();
}
var BRAND_SPACE_STUDIO_MIRROR_BATCH_STATUSES = ["preparing", "incomplete"];
function deriveBrandSpaceStudioMirrorBatchStatus(batch) {
  if (!batch) return null;
  if (batch.preparing > 0) return { status: "preparing", count: batch.preparing };
  if (batch.settled && batch.abandoned > 0) {
    return { status: "incomplete", count: batch.abandoned };
  }
  return null;
}
var BRAND_SPACE_SLUG_AVAILABILITY_REASONS = [
  "available",
  "current",
  "invalid",
  "reserved",
  "taken"
];

// src/brand-space/home.ts
var BRAND_SPACE_HOME_API_PATH = "/dashboard/brand-spaces";

// src/brand-space/application.ts
var BRAND_SPACE_APPLICATION_CHALLENGE_API_PATH = "/internal/brand-spaces/applications/challenge";
var BRAND_SPACE_APPLICATIONS_API_PATH = "/internal/brand-spaces/applications";
var BRAND_SPACE_DASHBOARD_APPLICATIONS_API_PATH = "/dashboard/brand-spaces/applications";
var BRAND_SPACE_VISITOR_IP_HEADER = "x-brand-space-visitor-ip";
var BRAND_SPACE_VISITOR_USER_AGENT_HEADER = "x-brand-space-visitor-user-agent";
var BRAND_SPACE_APPLICATION_IDENTITY_LIMITS = {
  firstName: 120,
  lastName: 120,
  email: 255,
  /**
   * Wide because the field also accepts a full profile URL, not only a handle
   * — Instagram itself caps a username at 30 characters.
   */
  instagramHandle: 2048
};
var BRAND_SPACE_APPLICATION_PROOF_LIMITS = {
  challengeToken: 1024,
  website: 255
};
var BRAND_SPACE_APPLICATION_ANSWERS_MAX_BYTES = 2e4;
var BRAND_SPACE_APPLICATION_STATE = {
  PENDING: "pending",
  ACCEPTED: "accepted",
  REJECTED: "rejected"
};
var BRAND_SPACE_APPLICATION_TABLE_SORT_FIELDS = ["submittedAt"];
var BRAND_SPACE_APPLICATION_DELIVERY_STATUS = {
  NOT_REQUIRED: "not_required",
  PENDING: "pending",
  PROCESSING: "processing",
  /** A provider attempt may have left; technical recovery must not send it again. */
  UNKNOWN: "unknown",
  SENT: "sent",
  SUPPRESSED: "suppressed",
  SKIPPED_NON_PRODUCTION: "skipped_non_production",
  PERMANENT_FAILURE: "permanent_failure",
  /**
   * Reported by the email provider after the message left. They refine SENT
   * and never regress: a delivery arriving after a click keeps the click.
   */
  DELIVERED: "delivered",
  CLICKED: "clicked",
  BOUNCED: "bounced",
  COMPLAINED: "complained"
};
function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}
function isBlank(value) {
  if (Array.isArray(value)) return value.length === 0;
  return typeof value === "string" && value.trim().length === 0;
}
function checkAnswerValue(question, value) {
  switch (question.kind) {
    case "short":
    case "long": {
      if (typeof value !== "string") return "invalid_type";
      const limit = question.kind === "short" ? BRAND_SPACE_LIMITS.answerShort : BRAND_SPACE_LIMITS.answerLong;
      return isBrandSpaceGraphemeBoundedString(value, limit) ? null : "too_long";
    }
    case "choice": {
      if (typeof value !== "string") return "invalid_type";
      return question.options.includes(value) ? null : "unknown_option";
    }
    case "multi": {
      if (!Array.isArray(value)) return "invalid_type";
      if (value.some((entry) => typeof entry !== "string")) return "invalid_type";
      if (value.some((entry) => !question.options.includes(entry))) return "unknown_option";
      return new Set(value).size === value.length ? null : "duplicate_option";
    }
    case "number": {
      if (typeof value !== "number") return "invalid_type";
      return Number.isFinite(value) && value >= 0 && value <= BRAND_SPACE_LIMITS.answerNumber ? null : "invalid_number";
    }
    case "url": {
      if (typeof value !== "string") return "invalid_type";
      return isSafeBrandSpaceWebUrl(value, BRAND_SPACE_LIMITS.answerUrl) ? null : "invalid_url";
    }
  }
}
function validateBrandSpaceApplicationAnswers(questions, answers) {
  if (answers === void 0 || answers === null) {
    return { ok: true, answers: [], customAnswers: [] };
  }
  if (!Array.isArray(answers)) {
    return { ok: false, issues: [{ key: null, code: "malformed" }] };
  }
  const issues = [];
  const declared = new Map(questions.map((question) => [question.key, question]));
  const received = /* @__PURE__ */ new Map();
  for (const entry of answers) {
    if (!isRecord(entry) || Object.keys(entry).length !== 2 || typeof entry.key !== "string" || !("value" in entry)) {
      return { ok: false, issues: [{ key: null, code: "malformed" }] };
    }
    const value = entry.value;
    if (!declared.has(entry.key)) {
      issues.push({ key: entry.key, code: "unknown_question" });
      continue;
    }
    if (received.has(entry.key)) {
      issues.push({ key: entry.key, code: "duplicate_answer" });
      continue;
    }
    received.set(entry.key, value);
  }
  const oversized = [];
  for (const [key, value] of received) {
    if (brandSpaceJsonByteLength({ key, value }) > BRAND_SPACE_APPLICATION_ANSWERS_MAX_BYTES) {
      oversized.push({ key, code: "answers_too_large" });
    }
  }
  if (oversized.length > 0) return { ok: false, issues: [...issues, ...oversized] };
  const weighed = [...received].map(([key, value]) => ({ key, value }));
  if (brandSpaceJsonByteLength(weighed) > BRAND_SPACE_APPLICATION_ANSWERS_MAX_BYTES) {
    return { ok: false, issues: [...issues, { key: null, code: "answers_too_large" }] };
  }
  const accepted = [];
  const customAnswers = [];
  for (const question of questions) {
    const value = received.get(question.key);
    if (value === void 0 || isBlank(value)) {
      if (question.required) issues.push({ key: question.key, code: "required" });
      continue;
    }
    const code = checkAnswerValue(question, value);
    if (code) {
      issues.push({ key: question.key, code });
      continue;
    }
    accepted.push({ key: question.key, value });
    customAnswers.push({
      key: question.key,
      label: question.label,
      kind: question.kind,
      value
    });
  }
  if (issues.length > 0) return { ok: false, issues };
  return { ok: true, answers: accepted, customAnswers };
}
var BRAND_SPACE_APPLICATION_DECISION_OUTCOMES = [
  "accepted_created",
  "accepted_existing_invitation",
  "already_accepted",
  "rejected",
  "already_rejected"
];

// src/brand-space/admin-actor-assertion.ts
var BRAND_SPACE_EMERGENCY_UNPUBLISH_ACTOR_ASSERTION_DOMAIN = "brand-space-emergency-unpublish-v1";
function retainBrandSpaceEmergencyUnpublishAttempt(previous, command, createKey) {
  const fingerprint = JSON.stringify([
    command.brandSpaceId,
    command.canonicalUrlConfirmation,
    command.reason.trim()
  ]);
  if (previous?.fingerprint === fingerprint) return previous;
  return { fingerprint, idempotencyKey: createKey() };
}
function buildBrandSpaceEmergencyUnpublishActorAssertionPayload(input) {
  if (!/^[a-f0-9]{64}$/.test(input.reasonDigest)) {
    throw new TypeError("Brand Space emergency reason digest must be lowercase SHA-256 hex");
  }
  return [
    BRAND_SPACE_EMERGENCY_UNPUBLISH_ACTOR_ASSERTION_DOMAIN,
    "POST",
    input.brandSpaceId,
    input.idempotencyKey,
    input.actorIdentity,
    input.canonicalUrlConfirmation,
    input.reasonDigest
  ].join("\n");
}

export {
  brandSpaceGraphemes,
  exceedsBrandSpaceGraphemeLimit,
  normalizeBrandSpaceText,
  truncateBrandSpaceMetadataText,
  clampBrandSpaceDisplayText,
  BRAND_SPACE_PUBLIC_LOCALES,
  BRAND_SPACE_FALLBACK_LOCALE,
  isBrandSpacePublicLocale,
  coerceBrandSpacePublicLocale,
  BRAND_SPACE_CONFIG_VERSION,
  BRAND_SPACE_SECTION_KEYS,
  BRAND_SPACE_LAYOUTS,
  BRAND_SPACE_FONT_PAIRINGS,
  BRAND_SPACE_LINK_ICON_IDS,
  BRAND_SPACE_CREATOR_PLATFORMS,
  BRAND_SPACE_APPLICATION_QUESTION_KINDS,
  BRAND_SPACE_LIMITS,
  BRAND_SPACE_PROFILE_DESCRIPTION_DISPLAY_LIMIT,
  BRAND_SPACE_DEFAULT_COPY,
  DEFAULT_BRAND_SPACE_CONFIG,
  createDefaultBrandSpaceConfig,
  BrandSpaceConfigUpgradeError,
  isBrandSpaceGraphemeBoundedString,
  isSafeBrandSpaceWebUrl,
  brandSpaceOffersApplications,
  hasUniqueBrandSpaceItemKeys,
  hasUniqueBrandSpaceLinkKeys,
  BRAND_SPACE_ITEM_KEY_PATTERN,
  isBrandSpaceItemKey,
  isBrandSpaceApplicationQuestion,
  isBrandSpaceProgramId,
  isBrandSpaceConfig,
  isBrandSpaceConfigV4,
  isBrandSpaceConfigV3,
  isBrandSpaceConfigV2,
  isBrandSpaceConfigV1,
  upgradeBrandSpaceConfig,
  BRAND_SPACE_OVERRIDE_PATHS,
  BrandSpaceOverrideError,
  upgradeBrandSpaceConfigOverride,
  mergeBrandSpaceConfig,
  diffBrandSpaceConfig,
  removeBrandSpaceOverride,
  countBrandSpaceOverrides,
  BRAND_SPACE_ADDRESS_MAX_LENGTH,
  BRAND_SPACE_CONFIG_MAX_BYTES,
  BRAND_SPACE_OVERRIDE_MAX_BYTES,
  BRAND_SPACE_CONFIG_DB_MAX_BYTES,
  BRAND_SPACE_OVERRIDE_DB_MAX_BYTES,
  BRAND_SPACE_STATUSES,
  BRAND_SPACE_RESERVED_SEGMENTS,
  classifyBrandSpaceAddressSegment,
  normalizeBrandSpaceAddressSegment,
  BRAND_SPACE_PROGRAMME_SEGMENT,
  parseBrandSpacePublicAddress,
  BRAND_SPACE_DEFAULT_PUBLIC_ORIGIN,
  BrandSpaceStorageError,
  brandSpaceUtf8ByteLength,
  brandSpaceJsonByteLength,
  validateBrandSpaceConfigStorage,
  validateBrandSpaceOverrideStorage,
  BRAND_SPACE_PUBLIC_API_PATH,
  BRAND_SPACE_INTERNAL_API_PATH,
  BRAND_SPACE_SITEMAP_API_PATH,
  BRAND_SPACE_VARIANT_V1_CAP,
  BRAND_SPACE_REVALIDATION_ADDRESS_CAP,
  BRAND_SPACE_SITEMAP_ENTRY_CAP,
  BRAND_SPACE_PRESENTATION_LIMITS,
  BRAND_SPACE_LEGACY_PUBLIC_PATH,
  BRAND_SPACE_VARIANTS_API_SEGMENT,
  BRAND_SPACE_PREVIEW_API_SEGMENT,
  BRAND_SPACE_PREVIEW_TOKEN_API_PATH,
  BRAND_SPACE_PREVIEW_HEADER,
  BRAND_SPACE_PUBLIC_CACHE_CONTROL,
  BRAND_SPACE_PRIVATE_CACHE_CONTROL,
  toBrandSpacePublicConfig,
  validateBrandSpacePublicConfigStorage,
  buildBrandSpacePublicPath,
  buildBrandSpaceLegacyPublicPath,
  HOSTED_MENTION_THUMBNAIL_PATH,
  HOSTED_MENTION_THUMBNAIL_FILENAME,
  HOSTED_MENTION_VIDEO_FILENAME,
  buildHostedMentionThumbnailUrl,
  buildHostedMentionPath,
  buildHostedMentionVideoUrl,
  brandSpaceHarvestedPlatforms,
  isBrandSpaceMediaHost,
  validateBrandSpaceCreatorContentPresentation,
  BRAND_SPACE_PREVIEW_CHANNEL,
  BRAND_SPACE_PREVIEW_VERSION,
  BRAND_SPACE_PREVIEW_MESSAGE_MAX_BYTES,
  BrandSpacePreviewProtocolError,
  isBrandSpacePreviewInstanceId,
  normalizeBrandSpaceStudioOrigin,
  createBrandSpacePreviewReadyMessage,
  createBrandSpacePreviewAppliedMessage,
  createBrandSpacePreviewRenderMessage,
  parseBrandSpacePreviewControlMessage,
  parseBrandSpacePreviewRenderMessage,
  BRAND_SPACE_STUDIO_API_PATH,
  BRAND_SPACE_STUDIO_MENTIONS_API_PATH,
  BRAND_SPACE_STUDIO_DRAFT_API_PATH,
  BRAND_SPACE_STUDIO_SLUG_AVAILABILITY_API_PATH,
  BRAND_SPACE_STUDIO_PUBLISH_API_PATH,
  BRAND_SPACE_STUDIO_VARIANTS_API_PATH,
  BRAND_SPACE_STUDIO_MENTION_SORTS,
  BRAND_SPACE_STUDIO_MENTIONS_PAGE_SIZE,
  BRAND_SPACE_STUDIO_MENTIONS_MAX_PAGE,
  BRAND_SPACE_STUDIO_MENTION_THUMBNAIL_KINDS,
  toBrandSpaceStudioCreatorContentCandidate,
  normalizeBrandSpaceMentionHandleQuery,
  BRAND_SPACE_STUDIO_MIRROR_BATCH_STATUSES,
  deriveBrandSpaceStudioMirrorBatchStatus,
  BRAND_SPACE_SLUG_AVAILABILITY_REASONS,
  BRAND_SPACE_HOME_API_PATH,
  BRAND_SPACE_APPLICATION_CHALLENGE_API_PATH,
  BRAND_SPACE_APPLICATIONS_API_PATH,
  BRAND_SPACE_DASHBOARD_APPLICATIONS_API_PATH,
  BRAND_SPACE_VISITOR_IP_HEADER,
  BRAND_SPACE_VISITOR_USER_AGENT_HEADER,
  BRAND_SPACE_APPLICATION_IDENTITY_LIMITS,
  BRAND_SPACE_APPLICATION_PROOF_LIMITS,
  BRAND_SPACE_APPLICATION_ANSWERS_MAX_BYTES,
  BRAND_SPACE_APPLICATION_STATE,
  BRAND_SPACE_APPLICATION_TABLE_SORT_FIELDS,
  BRAND_SPACE_APPLICATION_DELIVERY_STATUS,
  validateBrandSpaceApplicationAnswers,
  BRAND_SPACE_APPLICATION_DECISION_OUTCOMES,
  BRAND_SPACE_EMERGENCY_UNPUBLISH_ACTOR_ASSERTION_DOMAIN,
  retainBrandSpaceEmergencyUnpublishAttempt,
  buildBrandSpaceEmergencyUnpublishActorAssertionPayload
};
